/**
 * Gets a new url string where the old locale is replaced with the new locale query parameter.
 * @param fromLocale
 * @param toLocale
 * @param url
 * @returns {string} A new url where the old locale is replaced with the new locale query parameter
 */
function getUrlWithNewLocaleDevelopment(
  fromLocale: string,
  toLocale: string,
  url: string
): string {
  fromLocale = dashToUnderscore(fromLocale);
  const fromLocaleUpperOrLowercase = new RegExp(fromLocale, 'gi');
  toLocale = dashToUnderscore(toLocale).toLocaleLowerCase();
  return url.replace(fromLocaleUpperOrLowercase, toLocale);
}

/**
 * Checks to see if the url contains localhost and assumes it is in development
 * environment if it does.
 * @param url
 * @returns A boolean value indicating if we are in development mode
 */
function inLocalhostMode(url: string): boolean {
  return url.includes('localhost') || url.includes('127.0.0.1');
}

/**
 * Changes the url from the old locale to the new locale for the production environment.
 * @param locale
 * @param url
 * @returns {string} A url that contains the new locale
 */
function getUrlWithNewLocaleProduction(locale: string, url: string): string {
  locale = dashToUnderscore(locale);
  const appUrl = new URL(url);
  appUrl.searchParams.set(
    'locale',
    dashToUnderscore(locale).toLocaleLowerCase()
  );
  const comma = ',';
  const commaASCIIRegExp = /%2C/g;
  return appUrl.href.replace(commaASCIIRegExp, comma);
}

/**
 * Handles updating the window with the new url/locale.
 * @param newLocale
 */
function updateLocale(newLocale: string): void {
  window.location.href = newLocale;
  if (!inLocalhostMode(newLocale)) return;
  window.location.reload();
}
/**
 * Gets a new url based on the toLocale string and then replaces the current url
 * with the new url/locale and then reloads the window.
 * @param fromLocale
 * @param toLocale
 * @param url
 */
export function changeLocale(
  url: string,
  fromLocale: string,
  toLocale: string
) {
  const newLocale = inLocalhostMode(url)
    ? getUrlWithNewLocaleDevelopment(fromLocale, toLocale, url)
    : getUrlWithNewLocaleProduction(toLocale, url);
  updateLocale(newLocale);
}
/**
 * Replaces the dash with an underscore in a locale string.
 * @param locale
 * @returns {string} A locale string in lowercase form where the dash is replaced with and underscore.
 */
function dashToUnderscore(locale: string): string {
  return locale.replace('-', '_');
}
